import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Delete } from 'lucide-react';
import { Button } from '../ui/Button';

interface PINPadProps {
  onSubmit: (pin: string) => void;
  loading?: boolean;
  error?: string;
}

export const PINPad: React.FC<PINPadProps> = ({ onSubmit, loading, error }) => {
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);

  const handleNumberClick = (number: string) => {
    if (pin.length < 4) {
      setPin(prev => prev + number);
    }
  };

  const handleBackspace = () => {
    setPin(prev => prev.slice(0, -1));
  };

  const handleSubmit = () => {
    if (pin.length === 4) {
      onSubmit(pin);
    }
  };

  const handleClear = () => {
    setPin('');
  };

  const numbers = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['clear', '0', 'back']
  ];

  return (
    <div className="w-full max-w-sm sm:max-w-md mx-auto">
      {/* PIN Display */}
      <div className="mb-6 sm:mb-8">
        <div className="text-center mb-4">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Enter PIN</h2>
          <p className="text-sm sm:text-base text-gray-600">Enter your 4-digit PIN to continue</p>
        </div>
        
        <div className="flex justify-center space-x-2 mb-3">
          {[...Array(4)].map((_, index) => (
            <motion.div
              key={index}
              initial={{ scale: 0.8 }}
              animate={{ scale: pin.length > index ? 1.1 : 1 }}
              className={`
                w-10 h-10 sm:w-12 sm:h-12 rounded-lg border-2 flex items-center justify-center
                ${pin.length > index
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 bg-white'
                }
              `}
            >
              <span className="text-lg sm:text-xl font-bold text-gray-900">
                {pin.length > index ? (showPin ? pin[index] : '•') : ''}
              </span>
            </motion.div>
          ))}
        </div>
        
        <div className="text-center">
          <button
            onClick={() => setShowPin(!showPin)}
            className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 underline touch-manipulation"
          >
            {showPin ? 'Hide' : 'Show'} PIN
          </button>
        </div>
      </div>

      {/* Number Pad */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
        {numbers.flat().map((item, index) => {
          if (item === 'clear') {
            return (
              <motion.button
                key={index}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleClear}
                className="h-12 sm:h-14 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium text-xs sm:text-sm text-gray-700 transition-colors touch-manipulation"
              >
                Clear
              </motion.button>
            );
          }
          
          if (item === 'back') {
            return (
              <motion.button
                key={index}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleBackspace}
                className="h-12 sm:h-14 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium text-gray-700 transition-colors flex items-center justify-center touch-manipulation"
              >
                <Delete className="w-4 h-4 sm:w-5 sm:h-5" />
              </motion.button>
            );
          }
          
          return (
            <motion.button
              key={index}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleNumberClick(item)}
              className="h-12 sm:h-14 bg-white hover:bg-gray-50 border-2 border-gray-200 rounded-lg text-lg sm:text-xl font-bold text-gray-900 transition-colors touch-manipulation"
            >
              {item}
            </motion.button>
          );
        })}
      </div>

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg"
        >
          <p className="text-sm text-red-800 text-center">{error}</p>
        </motion.div>
      )}

      {/* Submit Button */}
      <Button
        onClick={handleSubmit}
        disabled={pin.length !== 4}
        loading={loading}
        className="w-full"
        size="lg"
      >
        Sign In
      </Button>
    </div>
  );
};